import { useInterval }         from "@mantine/hooks";
import dayjs                   from "dayjs";
import { useEffect, useState } from "react";

import { useGetIsPlaying }                                   from "_components/features/player/states";
import { STREAM_TIER }                                       from "_components/pages/proPage/constants";
import { useGetAdWatchStatus, useSetCanWatchMoreProContent } from "_jotai/rooterPro";
import { getUserRoles }                                      from "_services/api/pro";

import { useGetProfileData, useSetUserRoles } from "./auth";

const updatedUserRoles = async () => {
	try {
		const res = await getUserRoles ();

		return res;
	} catch ( error ) {
		// do nothing
	}
};

function useProContent ( {
	proUser,
	isProStream,
	remainingAdCount,
	remainingWatchDuration,
	streamTier,
	broadcastId,
	isEndedStream
} ) {
	const initialRemainingStreamTime = remainingWatchDuration;
	const isPlaying                  = useGetIsPlaying ();
	const userProfile                = useGetProfileData ();
	const adWatchStatus              = useGetAdWatchStatus ();
	const setUserRolesAtom           = useSetUserRoles ();
	const proEndTimeInSeconds        = dayjs ( userProfile?.userRoles?.[0]?.end ).diff ( dayjs (), "second" );

	const [ isLocalProUser, setLocalProUser ]             = useState ( proUser );
	const [ nonProRemainingTime, setNonProRemainingTime ] = useState ( initialRemainingStreamTime );
	const [ proRemainingTime, setProRemainingTime ]       = useState ( proEndTimeInSeconds );
	const [ checkProExpiring, setCheckProExpiring ]       = useState ( false );
	const setCanWatchMoreProContent                       = useSetCanWatchMoreProContent ();

	useEffect ( () => {
		setProRemainingTime ( proEndTimeInSeconds );

	}, [ proEndTimeInSeconds ] );

	const handleProExpiring = async () => {
		const freshUserRoles = await updatedUserRoles ();

		if ( freshUserRoles ) {

			setProRemainingTime ( dayjs ( freshUserRoles[0]?.end ).diff ( dayjs (), "second" ) );
		}

		setCheckProExpiring ( false );

		return;
	};

	const interval = useInterval ( async () => {
		if ( isLocalProUser ) {
			setProRemainingTime ( s => {
				if ( s === 5 ) {
					setCheckProExpiring ( true );
				}
				if ( s > 0 )
					return s - 1;

				setUserRolesAtom ( [] );
				setLocalProUser ( false );
				setCanWatchMoreProContent ( false );

				return s;
			} );
		} else {

			setNonProRemainingTime ( s => {
				if ( s > 0 )
					return s - 1;
				setCanWatchMoreProContent ( false );
				interval.stop ();

				return s;
			} );
		}
	}, 1000 );

	useEffect ( () => {
		if ( !isLocalProUser ) {

			if ( initialRemainingStreamTime <= 0 ) {
				if ( streamTier === STREAM_TIER.MID_STREAM && !adWatchStatus.finished && remainingAdCount ) {
					setCanWatchMoreProContent ( false );

					return;
				}
				setCanWatchMoreProContent ( false );

				return;
			}

			setCanWatchMoreProContent ( true );
			setNonProRemainingTime ( initialRemainingStreamTime );

			return interval.stop;
		}

		setCanWatchMoreProContent ( true );

		return interval.stop;

	}, [ initialRemainingStreamTime ] );

	useEffect ( () => {
		if ( adWatchStatus.finished ) {
			interval.stop ();

			return;
		}

		if ( !remainingAdCount && STREAM_TIER.MID_STREAM === streamTier?.toString () || adWatchStatus.opened ) {
			return;
		}

		if ( !isProStream ) {
			return;
		}

		if ( isEndedStream ) {

			interval.start ();

			return;
		}

		if ( isPlaying && !interval.active ) {
			interval.start ();

			return;
		}

		if ( !isPlaying && interval.active ) {
			interval.stop ();

			return;
		}

	}, [ isPlaying, isLocalProUser, adWatchStatus.finished, adWatchStatus.opened, broadcastId ] );

	useEffect ( () => {
		setLocalProUser ( proUser );
	}, [ proUser ] );

	useEffect ( () => {
		if ( checkProExpiring )
			handleProExpiring ();
	}, [ checkProExpiring ] );

	const proExpiring = proRemainingTime < ( 30 * 60 ) && isLocalProUser;

	return (
		{
			proExpiring,
			isProUser           : isLocalProUser,
			nonProRemainingTime : isLocalProUser ? proRemainingTime : nonProRemainingTime
		}
	);
}

export default useProContent;
